// SPDX-License-Identifier: AGPL-3.0-or-later
// Domain jargon, defined for someone who has never encountered any of it.
// Rendered as click-to-open popovers via [data-term] spans.
export interface Term { term: string; definition: string }

export const GLOSSARY: Record<string, Term> = {
  prisoner: {
    term: 'Prisoner',
    definition:
      'A person held in adult custody in a corrective-services facility on the night of 30 June — whether already sentenced or held on remand awaiting the outcome of their case. It does not include people in police cells (except where those are run by corrective services), youth detention, or immigration detention.',
  },
  imprisonmentRate: {
    term: 'Imprisonment rate',
    definition:
      'The number of prisoners per 100,000 adults. It is what makes a big state comparable to a small one: raw prisoner counts mostly track how many people live somewhere, while the rate is the share of the adult population in prison. Australia’s overall rate is about 216 per 100,000.',
  },
  crude: {
    term: 'Crude rate',
    definition:
      'An imprisonment rate calculated straight from the population, with no adjustment. Because Aboriginal and Torres Strait Islander people are on average younger — and imprisonment peaks in the 25–39 age range — a crude comparison between population groups is partly a comparison of their age structures.',
  },
  ageStandardised: {
    term: 'Age-standardised rate',
    definition:
      'A rate recalculated as if every group had the same age structure, so differences in how old the populations are can’t distort the comparison. It is the fair way to compare imprisonment between Aboriginal and Torres Strait Islander and non-Indigenous Australians — and the gap it shows (about 15–17 times) is barely smaller than the crude gap, so age structure is not what explains it.',
  },
  overRep: {
    term: 'Over-representation',
    definition:
      'When a group makes up a far larger share of prisoners than of the general population. Aboriginal and Torres Strait Islander people are about 3.8% of Australia’s adult population but around 37% of its prisoners. The over-representation ratio on this site is the age-standardised Aboriginal and Torres Strait Islander imprisonment rate divided by the non-Indigenous rate.',
  },
  atsi: {
    term: 'Aboriginal and Torres Strait Islander',
    definition:
      'The ABS records whether each prisoner identifies as being of Aboriginal and/or Torres Strait Islander origin. This site uses the ABS categories and terminology throughout. The data describes imprisonment — a measure of the justice system’s outcomes — not offending, and no group or place is presented here as a scoreboard.',
  },
  remand: {
    term: 'Remand (unsentenced)',
    definition:
      'A prisoner held in custody who has not been sentenced — usually awaiting trial or sentencing, and legally presumed innocent. Australia’s remand population has grown far faster than the sentenced population: it is now more than 4 in 10 prisoners, up from about 3 in 10 in 2016.',
  },
  sentenced: {
    term: 'Sentenced',
    definition:
      'A prisoner serving a term of imprisonment imposed by a court after being found guilty. The counterpart to remand (unsentenced). A small number of prisoners have another legal status (for example post-sentence detention) or an unknown status.',
  },
  mso: {
    term: 'Most serious offence/charge',
    definition:
      'A prisoner is counted under a single offence — the most serious one they are sentenced or charged with — not every offence on their record. So the offence breakdown understates less-serious offending: someone imprisoned mainly for assault who also committed theft appears only under assault.',
  },
  anzsoc: {
    term: 'ANZSOC',
    definition:
      'The Australian and New Zealand Standard Offence Classification — the official scheme that sorts offences into 16 divisions (homicide, assault, sexual assault, robbery, drug offences and so on). It’s what lets offence counts be compared consistently across states and years.',
  },
  prior: {
    term: 'Prior imprisonment',
    definition:
      'Whether a prisoner has been in adult prison before (under sentence) at any time in the past. About 6 in 10 Australian prisoners have prior imprisonment — a rough indicator of how often people return to custody.',
  },
  perturbation: {
    term: 'Perturbation',
    definition:
      'A small, deliberate random adjustment the ABS applies to cell counts to protect the privacy of individuals in small groups. It means the parts of a table may not add exactly to the published total — differences of a handful of prisoners here and there are this, not an error.',
  },
};

/** Wrap a term in an info-icon trigger. `key` must exist in GLOSSARY. */
export function gloss(key: string, label?: string): string {
  const t = GLOSSARY[key];
  if (!t) return label ?? key;
  return `<span class="glossary-link" data-term="${key}" tabindex="0" role="button" aria-label="Definition of ${t.term}">${label ?? t.term}<span class="gloss-icon" aria-hidden="true">i</span></span>`;
}

let popover: HTMLDivElement | null = null;
function ensurePopover(): HTMLDivElement {
  if (!popover) {
    popover = document.createElement('div');
    popover.className = 'glossary-popover';
    popover.setAttribute('role', 'dialog');
    document.body.appendChild(popover);
  }
  return popover;
}
export function hideGlossary(): void {
  popover?.classList.remove('visible');
}
function show(trigger: Element): void {
  const key = trigger.getAttribute('data-term') ?? '';
  const t = GLOSSARY[key];
  if (!t) return;
  const el = ensurePopover();
  el.innerHTML = `<h4></h4><p></p>`;
  (el.querySelector('h4') as HTMLElement).textContent = t.term;
  (el.querySelector('p') as HTMLElement).textContent = t.definition;
  el.classList.add('visible');
  const r = trigger.getBoundingClientRect();
  const pr = el.getBoundingClientRect();
  let left = r.left;
  let top = r.bottom + 8;
  if (left + pr.width > window.innerWidth - 12) left = window.innerWidth - pr.width - 12;
  if (top + pr.height > window.innerHeight - 12) top = r.top - pr.height - 8;
  el.style.left = `${Math.max(12, left)}px`;
  el.style.top = `${Math.max(12, top)}px`;
}
export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const trigger = (e.target as Element).closest('.glossary-link');
    if (trigger) { e.stopPropagation(); show(trigger); return; }
    if (!(e.target as Element).closest('.glossary-popover')) hideGlossary();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideGlossary();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element)?.classList?.contains('glossary-link')) {
      e.preventDefault();
      show(e.target as Element);
    }
  });
  window.addEventListener('resize', hideGlossary);
}
